import { type ReportablePeriod, ReportablePeriods, ReportingPeriod, ReportingPeriodMap } from "@qendo/profile/reports";
import {
    addMonths,
    addWeeks,
    addYears,
    type DateArg,
    isSameDay,
    isThisWeek,
    startOfWeek,
    subMonths,
    subWeeks,
    subYears,
    toDate,
} from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { Columns } from "~/components/layout/Columns";
import { Subtitle } from "~/components/layout/text";
import { Flex, Gap, Items } from "~/constants/layout";
import { Radius, Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { formatMonthName, formatWeek, formatYearName } from "~/util/dates";
import { resolveDateForPeriod } from "~/util/qendo/reports";

export type ReportPeriodSelectorProps = {
    period: ReportablePeriod;
    onUpdatePeriod: (value: ReportablePeriod) => void;
    from: DateArg<Date> | null;
    onUpdateFrom: (value: DateArg<Date> | null) => void;
};

export const ReportPeriodSelector = ({ period, onUpdatePeriod, from, onUpdateFrom }: ReportPeriodSelectorProps) => {
    const { colours } = useTheme();

    const buttons = ReportablePeriods.map((p) => {
        const selected = p === period || (p === ReportingPeriod.weekly && period === ReportingPeriod.last_week);

        return (
            <Button
                title={ReportingPeriodMap[p]}
                key={p}
                size="lg"
                background={selected ? colours.tabs.selected : "transparent"}
                colour={selected ? colours.text["inverse-primary"] : colours.text.secondary}
                onPress={() => onUpdatePeriod(p)}
            />
        );
    });

    const placeholder = useMemo(() => {
        switch (period) {
            case ReportingPeriod.yearly:
                return `This year (${formatYearName(new Date())})`;
            case ReportingPeriod.monthly:
                return `This month (${formatMonthName(new Date())})`;
            default:
                return "Last 7 days";
        }
    }, [period]);

    const setDate = (date: DateArg<Date> | null) => {
        const selected = resolveDateForPeriod(period, date);

        if ((from && selected && !isSameDay(from, selected)) || (from && !selected) || (selected && !from)) {
            onUpdateFrom(selected);
        }

        if ((period === ReportingPeriod.weekly && !selected) || (period === ReportingPeriod.last_week && selected)) {
            onUpdatePeriod(selected ? ReportingPeriod.weekly : ReportingPeriod.last_week);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(setDate): changes every time
    // biome-ignore lint/correctness/useExhaustiveDependencies(from): we're only watching period changes
    useEffect(() => {
        setDate(resolveDateForPeriod(period, from));
    }, [period]);

    const formatter = useCallback(
        (date: DateArg<Date>) =>
            (period === ReportingPeriod.yearly && formatYearName(date)) ||
            (period === ReportingPeriod.monthly && formatMonthName(date)) ||
            (isThisWeek(date) ? `This week (${formatWeek(date)})` : formatWeek(date)),
        [period],
    );

    const previous = () => {
        const current = toDate(from ?? new Date());

        switch (period) {
            case ReportingPeriod.yearly:
                setDate(subYears(current, 1));
                break;
            case ReportingPeriod.monthly:
                setDate(subMonths(current, 1));
                break;
            case ReportingPeriod.last_week: {
                const start = startOfWeek(current);

                onUpdateFrom(
                    resolveDateForPeriod(
                        ReportingPeriod.weekly,
                        isSameDay(start, current) ? subWeeks(current, 1) : start,
                    ),
                );
                onUpdatePeriod(ReportingPeriod.weekly);
                break;
            }
            default:
                setDate(subWeeks(current, 1));
        }
    };
    const next = () => {
        if (!from) {
            return;
        }
        const current = toDate(from);

        switch (period) {
            case ReportingPeriod.yearly:
                setDate(addYears(current, 1));
                break;
            case ReportingPeriod.monthly:
                setDate(addMonths(current, 1));
                break;
            default:
                setDate(addWeeks(current, 1));
        }
    };

    return (
        <View style={Gap.sm}>
            <Columns
                items={buttons}
                style={{ backgroundColor: colours.tabs.background, padding: Space.xxs, borderRadius: Radius.lg }}
                gap={0}
            />
            <View style={[Flex.row, Gap.sm, Items.center]}>
                <Button icon="chevron-left" size="sm" variant="neutral-quiet" onPress={previous} />
                <Subtitle size="md" style={Flex.grow}>
                    {from ? formatter(from) : placeholder}
                </Subtitle>
                <Button icon="chevron-right" size="sm" variant="neutral-quiet" disabled={!from} onPress={next} />
            </View>
        </View>
    );
};
